/**
 * Player interaction panel (v49) — opened by looking at a nearby pilot and
 * pressing F. The face-to-face menu: TRADE (in-person escrow — the venue rule
 * accepts standing together, not just docking together), WHISPER, and INVITE
 * TO PARTY. Meridian language, like every panel added since the social suite.
 *
 * Names come straight out of the room (guests are only trimmed/length-capped
 * server-side), so everything interpolated here is escaped.
 */
import { mountMeridianRule } from "./meridian";
import type { MeridianLine } from "./meridianLine";

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

export interface PlayerPanelTarget {
  id: number;
  name: string;
}

export class PlayerPanel {
  visible = false;
  private el: HTMLElement;
  private bodyEl: HTMLElement;
  private rule: MeridianLine;
  private target: PlayerPanelTarget | null = null;

  constructor(
    private handlers: {
      onTrade: (target: PlayerPanelTarget) => void;
      onWhisper: (target: PlayerPanelTarget) => void;
      onPartyInvite: (target: PlayerPanelTarget) => void;
      onClose: () => void;
    },
  ) {
    this.el = document.createElement("div");
    this.el.id = "player-panel";
    this.el.className = "mrd-panel";
    this.el.style.zIndex = "42";
    this.el.innerHTML = `
      <div class="mrd-head">
        <span class="mrd-title" id="pp-name">pilot</span>
        <span class="mrd-hint">esc</span>
      </div>
      <div class="mrd-rule"></div>
      <div class="pp-body mrd-body"></div>`;
    document.body.appendChild(this.el);
    this.bodyEl = this.el.querySelector(".pp-body")!;
    this.rule = mountMeridianRule(this.el.querySelector(".mrd-rule")!, 0.36);

    this.bodyEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest("button[data-act]") as HTMLElement | null;
      if (!btn || !this.target) return;
      const t = this.target;
      const act = btn.dataset.act;
      // Every action leaves this panel — it hands off to trade/chat/party UI.
      this.close();
      if (act === "trade") this.handlers.onTrade(t);
      else if (act === "whisper") this.handlers.onWhisper(t);
      else if (act === "party") this.handlers.onPartyInvite(t);
    });
  }

  open(target: PlayerPanelTarget): void {
    this.target = target;
    this.visible = true;
    this.el.style.display = "flex";
    this.el.querySelector("#pp-name")!.textContent = esc(target.name);
    this.bodyEl.innerHTML = `
      <div class="mrd-row">
        <span class="mrd-who">trade<i class="gp-blurb">escrowed swap, face to face</i></span>
        <span class="mrd-acts end"><button class="mrd-act" data-act="trade">open</button></span>
      </div>
      <div class="mrd-row">
        <span class="mrd-who">whisper<i class="gp-blurb">a private line in chat</i></span>
        <span class="mrd-acts end"><button class="mrd-act" data-act="whisper">write</button></span>
      </div>
      <div class="mrd-row">
        <span class="mrd-who">party<i class="gp-blurb">travel together</i></span>
        <span class="mrd-acts end"><button class="mrd-act" data-act="party">invite</button></span>
      </div>`;
    this.rule.start();
    this.rule.stir(1);
  }

  close(): void {
    if (!this.visible) return;
    this.visible = false;
    this.target = null;
    this.el.style.display = "none";
    this.rule.stop();
    this.handlers.onClose();
  }
}
